import type { QuestChapterModel, QuestNode } from './model';

/** Pixels per FTB quest unit at zoom = 1. */
export const QUEST_UNIT_PX = 36;

export interface Camera {
  x: number;
  y: number;
  zoom: number;
}

export interface Viewport {
  width: number;
  height: number;
}

export function worldToScreen(cam: Camera, wx: number, wy: number): { x: number; y: number } {
  return {
    x: wx * QUEST_UNIT_PX * cam.zoom + cam.x,
    y: wy * QUEST_UNIT_PX * cam.zoom + cam.y,
  };
}

export function screenToWorld(cam: Camera, sx: number, sy: number): { x: number; y: number } {
  const scale = QUEST_UNIT_PX * cam.zoom;
  return {
    x: (sx - cam.x) / scale,
    y: (sy - cam.y) / scale,
  };
}

export function questScreenRadius(quest: QuestNode, zoom: number): number {
  return Math.max(8, quest.size * 13 * zoom);
}

export function clampZoom(zoom: number): number {
  return Math.min(4, Math.max(0.18, zoom));
}

export function zoomAtPoint(cam: Camera, sx: number, sy: number, nextZoom: number): Camera {
  const zoom = clampZoom(nextZoom);
  if (zoom === cam.zoom) return cam;
  const world = screenToWorld(cam, sx, sy);
  return {
    zoom,
    x: sx - world.x * QUEST_UNIT_PX * zoom,
    y: sy - world.y * QUEST_UNIT_PX * zoom,
  };
}

export function fitCameraToBounds(
  bounds: QuestChapterModel['bounds'],
  viewport: Viewport,
  padding = 48,
): Camera {
  const spanX = Math.max(2, bounds.maxX - bounds.minX);
  const spanY = Math.max(2, bounds.maxY - bounds.minY);
  const availW = Math.max(1, viewport.width - padding * 2);
  const availH = Math.max(1, viewport.height - padding * 2);
  const zoom = clampZoom(Math.min(availW / (spanX * QUEST_UNIT_PX), availH / (spanY * QUEST_UNIT_PX)));
  const cx = (bounds.minX + bounds.maxX) / 2;
  const cy = (bounds.minY + bounds.maxY) / 2;
  return {
    zoom,
    x: viewport.width / 2 - cx * QUEST_UNIT_PX * zoom,
    y: viewport.height / 2 - cy * QUEST_UNIT_PX * zoom,
  };
}

/**
 * Uniform grid for viewport culling + hit tests. O(k) where k is the
 * number of occupied cells overlapping the query, not the chapter size.
 */
export class SpatialHash {
  readonly cellSize: number;
  private cells = new Map<string, string[]>();
  private items = new Map<string, { x: number; y: number; r: number }>();

  constructor(cellSize = 2) {
    this.cellSize = cellSize;
  }

  clear(): void {
    this.cells.clear();
    this.items.clear();
  }

  insert(id: string, x: number, y: number, r: number): void {
    this.items.set(id, { x, y, r });
    const minC = this.coord(x - r);
    const maxC = this.coord(x + r);
    const minR = this.coord(y - r);
    const maxR = this.coord(y + r);
    for (let cx = minC; cx <= maxC; cx++) {
      for (let cy = minR; cy <= maxR; cy++) {
        const key = `${cx}:${cy}`;
        const bucket = this.cells.get(key);
        if (bucket) bucket.push(id);
        else this.cells.set(key, [id]);
      }
    }
  }

  query(minX: number, minY: number, maxX: number, maxY: number): string[] {
    const seen = new Set<string>();
    const minC = this.coord(minX);
    const maxC = this.coord(maxX);
    const minR = this.coord(minY);
    const maxR = this.coord(maxY);
    for (let cx = minC; cx <= maxC; cx++) {
      for (let cy = minR; cy <= maxR; cy++) {
        const bucket = this.cells.get(`${cx}:${cy}`);
        if (!bucket) continue;
        for (const id of bucket) seen.add(id);
      }
    }
    return Array.from(seen);
  }

  hit(wx: number, wy: number): string | null {
    const candidates = this.query(wx, wy, wx, wy);
    let best: string | null = null;
    let bestD = Infinity;
    for (const id of candidates) {
      const item = this.items.get(id);
      if (!item) continue;
      const dx = wx - item.x;
      const dy = wy - item.y;
      const d = dx * dx + dy * dy;
      if (d <= item.r * item.r && d < bestD) {
        best = id;
        bestD = d;
      }
    }
    return best;
  }

  private coord(v: number): number {
    return Math.floor(v / this.cellSize);
  }
}

export function buildQuestSpatialHash(model: QuestChapterModel): SpatialHash {
  const hash = new SpatialHash(2);
  for (const q of model.quests) {
    hash.insert(q.id, q.x, q.y, q.size * 0.65);
  }
  return hash;
}

export function visibleQuestIds(
  hash: SpatialHash,
  cam: Camera,
  viewport: Viewport,
  padWorld = 1.5,
): string[] {
  const tl = screenToWorld(cam, -20, -20);
  const br = screenToWorld(cam, viewport.width + 20, viewport.height + 20);
  return hash.query(
    Math.min(tl.x, br.x) - padWorld,
    Math.min(tl.y, br.y) - padWorld,
    Math.max(tl.x, br.x) + padWorld,
    Math.max(tl.y, br.y) + padWorld,
  );
}

export function collectRelatedIds(model: QuestChapterModel, questId: string): Set<string> {
  const related = new Set<string>([questId]);
  for (const edge of model.edges) {
    if (edge.from === questId) related.add(edge.to);
    if (edge.to === questId) related.add(edge.from);
  }
  return related;
}
